/**
 * Truncates a string to a specified length and appends a suffix if necessary.
 *
 * @param value - The string to be truncated.
 * @param length - The maximum length of the truncated string. Default is 150.
 * @param suffix - The suffix to append when the string is truncated.
 *                 Default is "..".
 *
 * @returns The truncated string with an optional suffix.
 */
export function truncateString(value: string, length = 150, suffix = "..") {
  if (!value) return ""
  return value.length <= length ? value : `${value.slice(0, length)}${suffix}`
}

/**
 * Non-breaking space used inside formatted numbers ("600 000") and between
 * number and unit ("50 %", "12 mill. kr") so values never wrap mid-number.
 * Per Språkrådet:
 * https://sprakradet.no/godt-og-korrekt-sprak/rettskriving-og-grammatikk/mellomrom/
 */
export const NBSP = "\u00A0"

/**
 * Locales the formatters can render. Follows the app language selector
 * (CurrentUser.ActiveLanguageCode): Norwegian per Språkrådet, Swedish per
 * Isof, English per en-GB convention. See umbrella issue #914 for the decided
 * formats. Norwegian and Swedish share grouping (NBSP), decimal comma, and the
 * space before % / units; only the compact abbreviations differ
 * (nb "mill."/"mrd." vs sv "mn"/"md").
 */
export type FormattingLocale = "nb-NO" | "sv-SE" | "en-GB"

export const DEFAULT_FORMATTING_LOCALE: FormattingLocale = "nb-NO"

/**
 * Module-level active locale so non-React call sites (chart tick formatters,
 * plain utils) format correctly without threading a locale parameter through
 * every layer. Set once at app boot from CurrentUser.ActiveLanguageCode (see
 * AppContextLoader); a language switch reloads texts and re-renders, so
 * consumers pick up the new value on the next render.
 */
let activeFormattingLocale: FormattingLocale = DEFAULT_FORMATTING_LOCALE

/**
 * Maps an app language code (CurrentUser.ActiveLanguageCode, BCP 47) to a
 * formatting locale. English variants format as en-GB, Swedish as sv-SE;
 * everything else (nb-NO, unknown, missing) falls back to Norwegian — most
 * users are Norwegian and the data is Norwegian.
 */
export function toFormattingLocale(
  languageCode: string | null | undefined,
): FormattingLocale {
  const code = languageCode?.toLowerCase()
  if (code?.startsWith("en")) return "en-GB"
  if (code?.startsWith("sv")) return "sv-SE"
  return "nb-NO"
}

export function setFormattingLocale(locale: FormattingLocale) {
  activeFormattingLocale = locale
}

export function getFormattingLocale(): FormattingLocale {
  return activeFormattingLocale
}

/**
 * Formats a number for the given locale, grouping the integer part and
 * **stripping trailing zeros** (so 5 and 5.0 both render "5").
 *
 * nb-NO (Språkrådet tall-tid-dato): groups in threes with a non-breaking
 * space, decimal comma. en-GB: comma grouping, decimal point.
 *
 * Examples (nb-NO / en-GB):
 * - `formatNumber(12000000)`  -> "12 000 000" / "12,000,000"
 * - `formatNumber(1234.5, 1)` -> "1 234,5" / "1,234.5"
 * - `formatNumber(5, 1)`      -> "5" / "5"  (trailing zero dropped)
 * - `formatNumber(NaN)`       -> ""
 *
 * For a fixed number of decimals that KEEPS trailing zeros (e.g. "5,0"), use
 * {@link formatNumberFixed}.
 *
 * @param value - The number to format.
 * @param maxDecimals - Max decimals to keep; trailing zeros are stripped. Default 0.
 * @param locale - Defaults to the active app locale.
 * @returns Empty string for non-finite input.
 */
export function formatNumber(
  value: number,
  maxDecimals = 0,
  locale: FormattingLocale = getFormattingLocale(),
): string {
  if (!Number.isFinite(value)) return ""
  const [integerPart, decimalPart = ""] = value.toFixed(maxDecimals).split(".")
  const sign = integerPart.startsWith("-") ? "-" : ""
  const groupSeparator = locale === "en-GB" ? "," : NBSP
  const decimalSeparator = locale === "en-GB" ? "." : ","
  const grouped = integerPart
    .replace("-", "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
  const decimals = decimalPart.replace(/0+$/, "")
  return `${sign}${grouped}${decimals ? `${decimalSeparator}${decimals}` : ""}`
}

/**
 * Like {@link formatNumber} but always shows **exactly** `decimals` fraction
 * digits, KEEPING trailing zeros. Use where a fixed precision reassures the
 * reader the value is exact (e.g. measurement labels), so "5 m" does not read
 * as less precise than "5,0 m".
 *
 * Examples (nb-NO / en-GB), contrast with formatNumber in parentheses:
 * - `formatNumberFixed(5, 1)`      -> "5,0" / "5.0"  (formatNumber -> "5")
 * - `formatNumberFixed(1234.5, 2)` -> "1 234,50" / "1,234.50"
 * - `formatNumberFixed(12000, 2)`  -> "12 000,00" / "12,000.00"
 * - `formatNumberFixed(NaN, 1)`    -> ""
 *
 * @returns Empty string for non-finite input.
 */
export function formatNumberFixed(
  value: number,
  decimals: number,
  locale: FormattingLocale = getFormattingLocale(),
): string {
  if (!Number.isFinite(value)) return ""
  const [integerPart, decimalPart = ""] = value.toFixed(decimals).split(".")
  const sign = integerPart.startsWith("-") ? "-" : ""
  const groupSeparator = locale === "en-GB" ? "," : NBSP
  const decimalSeparator = locale === "en-GB" ? "." : ","
  const grouped = integerPart
    .replace("-", "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
  return `${sign}${grouped}${decimals > 0 ? `${decimalSeparator}${decimalPart}` : ""}`
}

/**
 * Compact number for tight UI (chart axes, map widgets).
 *
 * nb-NO (Språkrådet forkortelser): "12 mill." / "1,2 mrd." with the
 * abbreviation period; below one million there is no Norwegian abbreviation,
 * so the full grouped number is returned ("600 000", never "600k").
 * sv-SE (Isof / TT-språket): "12 mn" / "1,2 md"; below one million the full
 * grouped number ("600 000").
 * en-GB: "12M" / "1.2B" (no space); below one million the full grouped
 * number ("600,000").
 *
 * @returns Empty string for non-finite input.
 */
export function compactNumber(
  value: number,
  maxDecimals = 1,
  locale: FormattingLocale = getFormattingLocale(),
): string {
  if (!Number.isFinite(value)) return ""
  const abs = Math.abs(value)
  if (locale === "nb-NO") {
    if (abs >= 1e9)
      return `${formatNumber(value / 1e9, maxDecimals, locale)}${NBSP}mrd.`
    if (abs >= 1e6)
      return `${formatNumber(value / 1e6, maxDecimals, locale)}${NBSP}mill.`
    return formatNumber(value, maxDecimals, locale)
  }
  if (locale === "sv-SE") {
    if (abs >= 1e9)
      return `${formatNumber(value / 1e9, maxDecimals, locale)}${NBSP}md`
    if (abs >= 1e6)
      return `${formatNumber(value / 1e6, maxDecimals, locale)}${NBSP}mn`
    return formatNumber(value, maxDecimals, locale)
  }
  if (abs >= 1e9) return `${formatNumber(value / 1e9, maxDecimals, locale)}B`
  if (abs >= 1e6) return `${formatNumber(value / 1e6, maxDecimals, locale)}M`
  return formatNumber(value, maxDecimals, locale)
}

/**
 * Full money: "12 000 000 kr" (nb) / "12,000,000 kr" (en).
 * Non-breaking space before the unit in both locales.
 */
export function formatNOK(
  value: number,
  locale: FormattingLocale = getFormattingLocale(),
): string {
  if (!Number.isFinite(value)) return ""
  return `${formatNumber(value, 0, locale)}${NBSP}kr`
}

/**
 * Compact money for tight UI: "12 mill. kr" / "1,2 mrd. kr" / "600 000 kr"
 * (nb), "12M kr" / "1.2B kr" / "600,000 kr" (en).
 */
export function formatCompactNOK(
  value: number,
  maxDecimals = 1,
  locale: FormattingLocale = getFormattingLocale(),
): string {
  if (!Number.isFinite(value)) return ""
  return `${compactNumber(value, maxDecimals, locale)}${NBSP}kr`
}

/**
 * Percent: "50 %" with a non-breaking space before the sign (Språkrådet /
 * Isof mellomrom) and decimal comma in nb-NO and sv-SE; "50%" glued with
 * decimal point in en-GB, per English convention.
 */
export function formatPercent(
  value: number,
  maxDecimals = 1,
  locale: FormattingLocale = getFormattingLocale(),
): string {
  if (!Number.isFinite(value)) return ""
  const sep = locale === "en-GB" ? "" : NBSP
  return `${formatNumber(value, maxDecimals, locale)}${sep}%`
}

/**
 * Organisation number as the national register writes it, told apart by
 * length so no country is needed: nine digits is Norwegian ("923 609 016",
 * Brønnøysund), ten is Swedish ("556677-8899", Bolagsverket). Any other
 * value comes back unchanged. Groups join with a non-breaking space so a
 * number never wraps.
 */
export function formatOrganizationNumber(value: string): string {
  const digits = value.replace(/[\s -]/g, "")
  if (/^\d{9}$/.test(digits))
    return `${digits.slice(0, 3)}${NBSP}${digits.slice(3, 6)}${NBSP}${digits.slice(6)}`
  if (/^\d{10}$/.test(digits)) return `${digits.slice(0, 6)}-${digits.slice(6)}`
  return value
}

/**
 * Phone number for reading: a Norwegian number (eight digits, with or
 * without +47 / 0047) in pairs, "+47 67 51 30 00". Any other number comes
 * back unchanged, since other plans group by area code.
 */
export function formatPhoneNumber(value: string): string {
  const trimmed = value.trim()
  const digits = trimmed.replace(/\D/g, "")
  const dial = trimmed.startsWith("+") ? `+${digits}` : digits
  const national = /^(?:\+47|0047)?(\d{8})$/.exec(dial)?.[1]
  if (!national) return trimmed
  const pairs = national.match(/\d{2}/g) ?? []
  const prefix = dial.length > 8 ? `+47${NBSP}` : ""
  return `${prefix}${pairs.join(NBSP)}`
}
